import { rem } from 'polished';
import React, { useContext, useState } from 'react';
import styled, { css } from 'styled-components';

import { FatalAttraction as FA } from '@vpr/tracking';

import { translate } from '@vimeo/i18n-runtime';
import { Button } from '@vimeo/iris/components';
import { PaperPlane, Search as SearchIcon } from '@vimeo/iris/icons';
import { themes } from '@vimeo/iris/themes';
import { Header } from '@vimeo/iris/typography';
import { Picture } from '@vimeo/viewer';

import { EmbedShowcaseContext } from '../../context/useEmbedShowcase';
import { Pictures } from '../../types';
import { getRawUploadedImageUrl } from '../../utils/helpers';
import Search from '../Search';
import { useSearchState } from '../SearchContext';
import TeamMenu from './TeamMenu';

type Props = {
  allowShare: boolean;
  avatar?: Picture;
  userName?: string;
  logo?: Pictures;
  name: string;
  openShare: VoidFunction;
  webCustomLogo: boolean;
  albumOwnerId?: number;
  teamRole?: string;
};

const LiveNav = ({
  allowShare,
  avatar,
  userName,
  logo,
  name,
  openShare,
  webCustomLogo,
  albumOwnerId,
  teamRole,
}: Props) => {
  const embedShowcaseData = useContext(EmbedShowcaseContext);

  const customLogoSrc = webCustomLogo && logo && getRawUploadedImageUrl(logo);
  const avatarSrcSet = avatar && avatar.sizes.map((thumb) => `${thumb.link} ${thumb.width}w`);
  const avatarSrc = avatar && avatar.sizes[0].link;
  const { isSearching } = useSearchState();
  const [searchBarIsShowing, setShowSearchBar] = useState<boolean>(false);

  function trackOpenSearch(role?: string) {
    FA.trackClick({
      container: 'showcase',
      component: 'search',
      keyword: 'open_search',
    });
    if (role) {
      FA.trackClick({
        container: 'team_home_page',
        component: 'search',
        keyword: 'open_search_' + role.toLowerCase(),
      });
    }
  }

  return (
    <Container isSearching={isSearching}>
      <SideSection>
        {!isSearching &&
          (customLogoSrc ? (
            <CustomLogoImg src={customLogoSrc} alt={name} />
          ) : (
            <Title>{name}</Title>
          ))}
      </SideSection>
      {customLogoSrc && (
        <CenterSection>
          <Title>{name}</Title>
        </CenterSection>
      )}
      <ShareAvatarContainer>
        {albumOwnerId && teamRole && !embedShowcaseData?.isShowcaseEmbeded && (
          <TeamMenu ownerId={albumOwnerId} userRole={teamRole} />
        )}
        {!searchBarIsShowing && (
          <Button
            aria-label={translate('Search')}
            format="secondary"
            theme={themes.dark}
            icon={<SearchIcon />}
            onClick={() => {
              trackOpenSearch(teamRole);
              setShowSearchBar(true);
            }}
          />
        )}
        {searchBarIsShowing && <Search setShowSearchBar={setShowSearchBar} teamRole={teamRole} />}
        {allowShare && (
          <Button
            aria-label={translate('Share')}
            format="basic"
            variant="hyperminimal"
            onClick={() => {
              if (teamRole) {
                FA.trackClick({
                  container: 'team_home_page',
                  component: 'share_modal',
                  keyword: 'open_share_' + teamRole.toLowerCase(),
                });
              }
              openShare();
            }}
            size="sm"
            icon={<ShareIcon />}
          />
        )}
        {avatarSrc && (
          <AvatarImg
            src={avatarSrc}
            srcSet={avatarSrcSet && avatarSrcSet.join(', ')}
            alt={userName}
          />
        )}
      </ShareAvatarContainer>
    </Container>
  );
};

type isSearching = { isSearching?: boolean };

const getSearchVisibility = ({ isSearching = false }: isSearching) => css`
  visibility: ${isSearching ? 'hidden' : 'inherit'};
`;

const CenterSection = styled.div`
  @media only screen and (max-width: 767px) {
    display: none;
  }
`;

export const ShareIcon = styled(PaperPlane)`
  path {
    fill: white;
  }
`;

const AvatarImg = styled.img`
  width: ${rem(40)};
  border-radius: 50%;
  margin-left: ${rem(15)};
`;

const SideSection = styled.div`
  width: 25%;
`;

const Title = styled(Header).attrs({ size: '5' })`
  color: white;
  display: inline-block;
  vertical-align: middle;
  margin: 0;
`;

const Container = styled.div<isSearching>`
  position: absolute;
  top: 0;
  left: 0;
  z-index: 1;
  padding: 0 ${rem(50)};
  display: flex;
  flex-flow: row;
  justify-content: space-between;
  align-items: center;
  width: 100%;
  height: ${rem(125)};
  ${AvatarImg}, ${CenterSection}, ${Title} {
    ${getSearchVisibility};
  }
`;

const CustomLogoImg = styled.img`
  width: ${rem(75)};
  vertical-align: middle;
`;

const ShareAvatarContainer = styled(SideSection)`
  @media only screen and (max-width: 1280px) {
    width: 33%;
  }
  display: flex;
  flex-flow: row;
  justify-content: flex-end;
  align-items: center;
`;

export default LiveNav;
